import { SelectField } from "@/components/SelectField";
import { US_STATE_OPTIONS } from "@/lib/labels";
import type { AddressForm } from "./types";
import { FieldWrap } from "./FieldWrap";
import { TF } from "./TF";

export function AddressFields({
  prefix,
  value,
  set,
  errorAt,
  required = true,
}: {
  prefix: string;
  value: AddressForm;
  set: (key: keyof AddressForm, v: string) => void;
  errorAt?: (field: string) => string | undefined;
  // Defaults to true (every existing caller needs it); pass false for an
  // optional-prefill context (e.g. the send-intake form's "fill some of
  // it" section) where the recipient completes whatever's left blank.
  required?: boolean;
}) {
  // Input ids match the validator's dotted-key form (e.g.,
  // "biz.address1") so the scroll-to-first-error focus + the
  // roll-up summary's anchor links resolve via document.getElementById
  // without a separate lookup table. The address1/city/state/zip
  // sub-fields are all required when the parent address is required;
  // validator gates submit accordingly.
  const a1Err = errorAt?.(`${prefix}.address1`);
  const cityErr = errorAt?.(`${prefix}.city`);
  const stateErr = errorAt?.(`${prefix}.stateCd`);
  const zipErr = errorAt?.(`${prefix}.zip`);
  return (
    <div className="space-y-3">
      <TF
        id={`${prefix}.address1`}
        label="Street address"
        value={value.address1}
        onChange={(v) => set("address1", v)}
        format="address"
        maxLength={200}
        required={required}
        error={a1Err}
      />
      <TF
        id={`${prefix}.address2`}
        label="Suite / unit"
        value={value.address2}
        onChange={(v) => set("address2", v)}
        format="address"
        maxLength={200}
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <div className="sm:col-span-2">
          <TF
            id={`${prefix}.city`}
            label="City"
            value={value.city}
            onChange={(v) => set("city", v)}
            format="address"
            maxLength={300}
            required={required}
            error={cityErr}
          />
        </div>
        <FieldWrap
          id={`${prefix}.stateCd`}
          label="State"
          required={required}
          error={stateErr}
        >
          <SelectField
            id={`${prefix}.stateCd`}
            value={value.stateCd}
            options={US_STATE_OPTIONS}
            onChange={(v) => set("stateCd", v)}
            ariaInvalid={!!stateErr}
            ariaDescribedBy={stateErr ? `${prefix}.stateCd-err` : undefined}
          />
        </FieldWrap>
        <TF
          id={`${prefix}.zip`}
          label="ZIP"
          value={value.zip}
          onChange={(v) => set("zip", v)}
          format="zip"
          required={required}
          error={zipErr}
        />
      </div>
    </div>
  );
}
